import { Route } from 'vue-router';
import { RoutingRouteEnum } from '../route.enum';
import { SubscrbtionEnumPackagesCodeEnum } from '../../subscription/enums/packages-code.enum';
import { userPermissionGuard } from './user-permission.guard';
import { appRouterFactory } from '../factory';
import UserStoreModule from '../../user/store/module';
import redirectToHermes from '../../common/helpers/redirect-to-hermes';

function parseAddonIdsFromQuery(query: Route['query']): string[] {
  const raw = query['addons[]'];
  if (raw === undefined || raw === null) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list.map((id) => String(id).trim()).filter(Boolean);
}

/** Same query key as subscription checkout: `contact-phone` */
function parseContactPhoneFromQuery(query: Route['query']): string | null {
  const raw = query['contact-phone'];
  if (raw === undefined || raw === null) return null;
  const v = Array.isArray(raw) ? raw[0] : raw;
  const s = String(v).trim();
  return s || null;
}

function isPurchasablesComplexQuery(query: Route['query']): boolean {
  return typeof query.purchasables === 'string' && query.purchasables.length > 0;
}

/**
 * Before subscription checkout loads: if the URL already has purchasable ids (addons[]) or package_code,
 * send the user straight to Hermes OTL — no merchant checkout screen and no card step here.
 * Other query shapes (e.g. purchasables) continue to the checkout component.
 */
export function subscriptionCheckoutHermesGuard(to: Route, from: Route, next: Function): void {
  userPermissionGuard(
    to,
    (result: unknown) => {
      if (result !== undefined) {
        next(result);
        return;
      }

      const queryPackageCode = to.query['package_code']?.toString();

      if (queryPackageCode) {
        const validPrefixes = [
          SubscrbtionEnumPackagesCodeEnum.rise,
          SubscrbtionEnumPackagesCodeEnum.newGrowth,
          SubscrbtionEnumPackagesCodeEnum.newProfessional,
        ];
        const isPackageAvailable = validPrefixes.some((prefix) => queryPackageCode.startsWith(prefix));

        if (!isPackageAvailable) {
          next({ name: RoutingRouteEnum.subscriptionPackages });
          return;
        }
      }

      const isWebSubscription = UserStoreModule?.data?.store.subscription.manager?.web ?? true;
      if (!isWebSubscription && to.query.package_code) {
        next({ name: RoutingRouteEnum.subscriptionsIndex });
        return;
      }

      const storeId = UserStoreModule.data?.store?.id;
      const addonIds = parseAddonIdsFromQuery(to.query);
      const hasPackageCode = Boolean(queryPackageCode);
      const contactPhone = parseContactPhoneFromQuery(to.query);

      const shouldHandOffToHermes =
        storeId && (addonIds.length > 0 || hasPackageCode) && !isPurchasablesComplexQuery(to.query);

      if (!shouldHandOffToHermes) {
        next();
        return;
      }

      void (async () => {
        try {
          const navigated = await redirectToHermes({
            storeId,
            purchaseId: null,
            packageCode: hasPackageCode ? queryPackageCode : null,
            addonsCheckout: addonIds.length > 0,
            addonIds: addonIds.length ? addonIds : null,
            contactPhone,
          });
          if (navigated) next(false);
          else next();
        } catch (e) {
          console.error('subscriptionCheckoutHermesGuard', e);
          next();
        }
      })();
    },
    appRouterFactory(),
  );
}
